import cardValidator from 'card-validator'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import type { Atom } from 'jotai'
import { z } from 'zod'
import { getYear } from '~/lib/date/date'
import { detectCardBrand, validateCardExpiration } from '~/lib/reservationInput/card'
import { getGMOCardToken } from '~/lib/reservationInput/gmo'
import { env$ } from '~/stores/env'
import { selectedCardStatus$ } from '../store/card'
import type { CardErrors } from '../store/lib/checkDisabled'
import { visitDate$ } from '../store/stateMachine/context'
import { transition$ } from '../store/stateMachine/state'
import { submitted$, tokenError$ } from './card-fusuma'
import { scope } from './scope'

export const cardNumber$ = scope.atom<string | undefined>(undefined, 'cardNumber$')

export function useSetCardNumber() {
  return useSetAtom(cardNumber$)
}

const cardNumberError$ = atom((get) => {
  return validateCardNumber(get(cardNumber$))
})
cardNumberError$.debugLabel = 'cardNumberError$'

export function useCardNumberError() {
  return useAtomValue(cardNumberError$)
}

const cardNumberSchema = z
  .string()
  .min(1, { message: 'カード番号は必須項目です' })
  .refine((v) => cardValidator.number(v).isValid, {
    message: 'カード番号が正しくありません。入力を確認してください。',
  })
function validateCardNumber(cardNumber?: string) {
  if (cardNumber === undefined) {
    return undefined
  }
  const result = cardNumberSchema.safeParse(cardNumber)
  if (!result.success) {
    return result.error.issues[0].message
  }
  return undefined
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('validateCardNumber', () => {
    expect(validateCardNumber()).toBe(undefined)
    expect(validateCardNumber('')).toBe('カード番号は必須項目です')
    expect(validateCardNumber('123')).toBe('カード番号が正しくありません。入力を確認してください。')
    expect(validateCardNumber('4242424242424242')).toBe(undefined)
  })
}

export const cardHolder$ = scope.atom<string | undefined>(undefined, 'cardHolder$')

export function useCardHolder() {
  return useAtomValue(cardHolder$)
}

export function useSetCardHolder() {
  return useSetAtom(cardHolder$)
}

const cardHolderError$ = atom((get) => {
  return validateCardHolder(get(cardHolder$))
})
cardHolderError$.debugLabel = 'cardHolderError$'

export function useCardHolderError() {
  return useAtomValue(cardHolderError$)
}

const cardHolderSchema = z.string().min(1, { message: 'カード名義は必須項目です' })
function validateCardHolder(cardHolder?: string) {
  if (cardHolder === undefined) {
    return undefined
  }
  const result = cardHolderSchema.safeParse(cardHolder)
  if (!result.success) {
    return result.error.issues[0].message
  }
  return undefined
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('validateCardHolder', () => {
    expect(validateCardHolder()).toBe(undefined)
    expect(validateCardHolder('')).toBe('カード名義は必須項目です')
    expect(validateCardHolder('a')).toBe(undefined)
  })
}

export const cardExpireMonth$ = scope.atom<string | undefined>(undefined, 'cardExpireMonth$')

export function useSetCardExpireMonth() {
  return useSetAtom(cardExpireMonth$)
}

export const cardExpireYear$ = scope.atom<string | undefined>(undefined, 'cardExpireYear$')

export function useSetCardExpireYear() {
  return useSetAtom(cardExpireYear$)
}

type Options = {
  month: string[]
  year: string[]
}
const cardExpirationOptions$: Atom<Options> = atom((get) => {
  const visitDate = get(visitDate$)
  if (visitDate === undefined) {
    return { month: [], year: [] }
  }
  const month = Array.from({ length: 12 }, (_, i) => `${i + 1}`)
  const yearSuffix = getYear(visitDate)
  const year = Array.from({ length: 20 }, (_, i) => `${Number(yearSuffix) + i}`.slice(-2))
  return { month, year }
})
cardExpirationOptions$.debugLabel = 'cardExpirationOptions$'

export function useCardExpirationOptions() {
  return useAtomValue(cardExpirationOptions$)
}

export const securityCode$ = scope.atom<string | undefined>(undefined, 'securityCode$')

export function useSetSecurityCode() {
  return useSetAtom(securityCode$)
}

const securityCodeError$ = atom((get) => {
  return validateSecurityCode(get(securityCode$), get(cardNumber$))
})
securityCodeError$.debugLabel = 'securityCodeError$'

export function useSecurityCodeError() {
  return useAtomValue(securityCodeError$)
}

const securityCodeSchema = z
  .object({ securityCode: z.string(), cardNumber: z.string() })
  .refine(
    (v) => {
      return v.securityCode.length > 1
    },
    { message: 'セキュリティコードは必須項目です' }
  )
  .refine(
    (v) => {
      // Amexのみ4桁のため分岐
      const maxLength = cardValidator.number(v.cardNumber).card?.code.size ?? 3
      return cardValidator.cvv(v.securityCode, maxLength).isValid
    },
    {
      message: 'セキュリティコードが正しくありません。入力を確認してください。',
    }
  )
function validateSecurityCode(securityCode?: string, cardNumber?: string) {
  if (securityCode === undefined || cardNumber === undefined) {
    return undefined
  }
  const result = securityCodeSchema.safeParse({ securityCode, cardNumber })
  if (!result.success) {
    return result.error.issues[0].message
  }
  return undefined
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('validateSecurityCode', () => {
    const cardNumber = '4242424242424242'
    expect(validateSecurityCode()).toBe(undefined)
    expect(validateSecurityCode('', cardNumber)).toBe('セキュリティコードは必須項目です')
    expect(validateSecurityCode('1234', cardNumber)).toBe(
      'セキュリティコードが正しくありません。入力を確認してください。'
    )
    expect(validateSecurityCode('123', cardNumber)).toBe(undefined)
  })
}

const cardExpirationError$ = atom((get) => {
  const year = get(cardExpireYear$)
  const month = get(cardExpireMonth$)
  const visitDate = get(visitDate$)
  return validateCardExpiration({ year, month, visitDate })
})
cardExpirationError$.debugLabel = 'cardExpirationError$'

export function useCardExpirationError() {
  return useAtomValue(cardExpirationError$)
}

export const isInputCompleted$ = atom((get) => {
  return Object.values([
    get(cardNumber$),
    get(cardHolder$),
    get(securityCode$),
    get(cardExpireMonth$),
    get(cardExpireYear$),
  ]).every((input) => input !== undefined && input !== '')
})
isInputCompleted$.debugLabel = 'isInputCompleted$'

export const errors$: Atom<CardErrors> = atom((get) => {
  return {
    cardNumberError: get(cardNumberError$),
    cardHolderError: get(cardHolderError$),
    securityCodeError: get(securityCodeError$),
    cardExpirationError: get(cardExpirationError$),
  }
})
errors$.debugLabel = 'errors$'

const userStatus$ = atom((get) => {
  return {
    card: {
      isInputCompleted: get(isInputCompleted$),
      errors: get(errors$),
      selected: get(selectedCardStatus$),
    },
  }
})
userStatus$.debugLabel = 'userStatus$'

type GMOTokenInputs = {
  apiKey: string
  cardNumber: string
  cardYM: string
  securityCode: string
  holderName: string
}

const getGMOCardTokenInputs$: Atom<GMOTokenInputs> = atom((get) => {
  return {
    apiKey: get(env$).CARD_PAYMENT_TOKEN_SERVICE_APIKEY,
    cardNumber: get(cardNumber$) ?? '',
    cardYM: `${get(cardExpireYear$)}${get(cardExpireMonth$)}`,
    securityCode: get(securityCode$) ?? '',
    holderName: get(cardHolder$) ?? '',
  }
})

export const getGMOCardTokenQuery$ = atom({
  fn: async ({ apiKey, cardNumber, cardYM, securityCode, holderName }: GMOTokenInputs) =>
    await getGMOCardToken(apiKey, cardNumber, cardYM, securityCode, holderName),
})

export const submit$ = atom(null, async (get, set) => {
  const selected = get(selectedCardStatus$)
  if (selected === undefined) {
    return
  }

  // 前回失敗したときのトークンエラーが残っている場合はクリア
  if (get(tokenError$) !== undefined) {
    set(tokenError$, undefined)
  }

  // 既存のカードを選択する場合はトークンを取得する必要がないため、updated event を発行する
  if (selected.type === 'registered') {
    set(transition$, {
      type: 'updated',
      card: { type: 'registered', cardId: selected.cardId },
    })
    set(submitted$, 'finished')
  }

  // 新しいカードを登録する場合はここでトークン取得を行い、取得に成功すれば updated event を発行する
  if (selected.type === 'new') {
    try {
      set(submitted$, 'pending')

      const inputs = get(getGMOCardTokenInputs$)
      const result = await get(getGMOCardTokenQuery$).fn(inputs)
      const cardBrand = detectCardBrand(get(cardNumber$) ?? '')
      if (cardBrand === undefined || result === undefined) {
        set(tokenError$, 'カードトークンの取得に失敗しました。')
        set(submitted$, 'initial')
        return
      }

      // tokenが取得できた = 新規カード登録ができた事と同意なので updated event を発行する
      set(transition$, {
        type: 'updated',
        card: {
          type: 'new',
          newCard: {
            maskedNumber: result.maskedCardNo.slice(-3),
            goodThru: `${get(cardExpireMonth$)}/${get(cardExpireYear$)}`,
            brand: cardBrand,
            holderName: inputs.holderName,
            token: result.token,
          },
        },
        userStatus: get(userStatus$),
      })
      set(submitted$, 'finished')
    } catch (e) {
      if (typeof e === 'string') {
        set(tokenError$, e)
        set(submitted$, 'initial')
      }
    }
  }
})
submit$.debugLabel = 'submit$'

export function useSetSubmit() {
  return useSetAtom(submit$)
}
